import { useState, useEffect, useMemo } from 'react';
import { Search, FileText, Download, PlusCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { listInvoices, getInvoiceStats } from '../../../entities/invoice';
import { listPayments } from '../../../entities/payment';

export function BillingPage() {
  const [activeTab, setActiveTab] = useState('invoices');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Invoices state
  const [invoices, setInvoices] = useState([]);
  const [invLoading, setInvLoading] = useState(false);
  const [invPage, setInvPage] = useState(1);
  const [invTotal, setInvTotal] = useState(0);
  const [invTotalPages, setInvTotalPages] = useState(1);
  const [invStatusFilter, setInvStatusFilter] = useState('all');

  // Payments state
  const [payments, setPayments] = useState([]);
  const [payLoading, setPayLoading] = useState(false);
  const [payPage, setPayPage] = useState(1);
  const [payTotal, setPayTotal] = useState(0);
  const [payTotalPages, setPayTotalPages] = useState(1);

  // Stats
  const [stats, setStats] = useState(null);

  const limit = 10;

  // Debounce
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(id);
  }, [search]);

  // Stats
  useEffect(() => {
    getInvoiceStats()
      .then(setStats)
      .catch(err => console.error('Failed to load billing stats:', err));
  }, []);

  // Invoice query
  const invQuery = useMemo(() => {
    const p = new URLSearchParams();
    p.set('page', String(invPage));
    p.set('limit', String(limit));
    if (debouncedSearch.trim()) p.set('q', debouncedSearch.trim());
    if (invStatusFilter !== 'all') p.set('status', invStatusFilter);
    p.set('sortBy', 'created_at');
    p.set('sortDir', 'desc');
    return `?${p.toString()}`;
  }, [invPage, debouncedSearch, invStatusFilter]);

  // Payment query
  const payQuery = useMemo(() => {
    const p = new URLSearchParams();
    p.set('page', String(payPage));
    p.set('limit', String(limit));
    if (debouncedSearch.trim()) p.set('q', debouncedSearch.trim());
    p.set('sortBy', 'created_at');
    p.set('sortDir', 'desc');
    return `?${p.toString()}`;
  }, [payPage, debouncedSearch]);

  // Fetch invoices
  useEffect(() => {
    if (activeTab !== 'invoices') return;
    setInvLoading(true);
    listInvoices(invQuery)
      .then(res => {
        setInvoices(res.data || []);
        setInvTotal(res.total || 0);
        setInvTotalPages(Math.ceil((res.total || 0) / limit));
      })
      .catch(() => toast.error('Failed to load invoices'))
      .finally(() => setInvLoading(false));
  }, [activeTab, invQuery]);

  // Fetch payments
  useEffect(() => {
    if (activeTab !== 'payments') return;
    setPayLoading(true);
    listPayments(payQuery)
      .then(res => {
        setPayments(res.data || []);
        setPayTotal(res.total || 0);
        setPayTotalPages(Math.ceil((res.total || 0) / limit));
      })
      .catch(() => toast.error('Failed to load payments'))
      .finally(() => setPayLoading(false));
  }, [activeTab, payQuery]);

  const statusColors = {
    Paid: 'bg-green-100 text-green-700',
    Pending: 'bg-yellow-100 text-yellow-700',
    Overdue: 'bg-red-100 text-red-700',
    Draft: 'bg-gray-200 text-gray-700',
  };

  const fmt = (n) => `$${Number(n || 0).toLocaleString()}`;

  return (
    <div className="p-6 w-full space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">Revenue Tracking</h2>
          <p className="text-sm text-gray-500">Monitor financial performance and manage payments</p>
        </div>
        <div className="flex gap-3">
          <button className="px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 text-sm">Export Report</button>
        </div>
      </div>

      {/* Top Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
          <p className="text-sm text-gray-500">Total Revenue</p>
          <h3 className="text-xl font-semibold text-gray-800">{stats ? fmt(stats.totalRevenue) : '-'}</h3>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
          <p className="text-sm text-gray-500">Loads Completed</p>
          <h3 className="text-xl font-semibold text-gray-800">{stats ? stats.completedLoads : '-'}</h3>
          <p className="text-xs text-gray-500">{stats ? `${fmt(stats.avgPerLoad)} avg/load` : ''}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
          <p className="text-sm text-gray-500">Paid Invoices</p>
          <h3 className="text-xl font-semibold text-gray-800">{stats ? stats.paidInvoices : '-'}</h3>
          <p className="text-xs text-gray-500">{stats ? `${stats.pendingInvoices} pending` : ''}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
          <p className="text-sm text-gray-500">Outstanding</p>
          <h3 className="text-xl font-semibold text-gray-800">{stats ? fmt(stats.outstanding) : '-'}</h3>
          <p className="text-xs text-gray-500">{stats ? `${stats.pendingInvoices} invoices` : ''}</p>
        </div>
      </div>

      {/* Filters + CTA */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input type="text" placeholder={`Search ${activeTab}...`} value={search} onChange={(e) => { setSearch(e.target.value); setInvPage(1); setPayPage(1); }} className="pl-9 pr-3 py-2 rounded-lg border border-gray-300 bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full" />
          </div>
          {activeTab === 'invoices' && (
            <select value={invStatusFilter} onChange={(e) => { setInvStatusFilter(e.target.value); setInvPage(1); }} className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm">
              <option value="all">All Status</option>
              <option value="Draft">Draft</option>
              <option value="Pending">Pending</option>
              <option value="Paid">Paid</option>
              <option value="Overdue">Overdue</option>
            </select>
          )}
        </div>
        <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2">
          <PlusCircle className="w-4 h-4" />
          Create Invoice
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b">
        <button onClick={() => setActiveTab('invoices')} className={`px-4 py-2 text-sm font-medium ${activeTab === 'invoices' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}>
          Invoices
        </button>
        <button onClick={() => setActiveTab('payments')} className={`px-4 py-2 text-sm font-medium ${activeTab === 'payments' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}>
          Payments
        </button>
      </div>

      {/* Invoices Table */}
      {activeTab === 'invoices' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {invLoading ? (
            <div className="p-8 text-center text-sm text-gray-500">Loading invoices...</div>
          ) : (
            <>
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 text-gray-600 font-medium">
                  <tr>
                    <th className="px-6 py-3">INVOICE ID</th>
                    <th className="px-6 py-3">LOAD ID</th>
                    <th className="px-6 py-3">CUSTOMER</th>
                    <th className="px-6 py-3">DRIVER</th>
                    <th className="px-6 py-3">AMOUNT</th>
                    <th className="px-6 py-3">DUE DATE</th>
                    <th className="px-6 py-3">STATUS</th>
                    <th className="px-6 py-3">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.length === 0 && (
                    <tr><td colSpan={8} className="px-6 py-8 text-center text-gray-500">No invoices found.</td></tr>
                  )}
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="border-t">
                      <td className="px-6 py-3 font-semibold text-gray-800">{inv.invoice_number}</td>
                      <td className="px-6 py-3">
                        <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">{inv.load_display_id || '-'}</span>
                      </td>
                      <td className="px-6 py-3">{inv.customer_name || '-'}</td>
                      <td className="px-6 py-3">{inv.driver_name || '-'}</td>
                      <td className="px-6 py-3 font-medium">{fmt(inv.amount)}</td>
                      <td className="px-6 py-3">{inv.due_date || '-'}</td>
                      <td className="px-6 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${statusColors[inv.status] || ''}`}>{inv.status}</span>
                      </td>
                      <td className="px-6 py-3 flex gap-2">
                        <FileText className="w-4 h-4 text-gray-500 cursor-pointer hover:text-blue-600" />
                        <Download className="w-4 h-4 text-gray-500 cursor-pointer hover:text-blue-600" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-t">
                <span className="text-sm text-gray-500">
                  Showing <span className="font-medium">{invoices.length === 0 ? 0 : (invPage - 1) * limit + 1}-{Math.min(invPage * limit, invTotal)}</span> of {invTotal}
                </span>
                <div className="flex items-center gap-1">
                  <button disabled={invPage <= 1} onClick={() => setInvPage(p => p - 1)} className="px-2 py-1 text-sm rounded hover:bg-gray-200 disabled:opacity-50">&lt;</button>
                  <span className="px-3 py-1 text-sm rounded bg-blue-600 text-white">{invPage}</span>
                  <button disabled={invPage >= invTotalPages} onClick={() => setInvPage(p => p + 1)} className="px-2 py-1 text-sm rounded hover:bg-gray-200 disabled:opacity-50">&gt;</button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Payments Table */}
      {activeTab === 'payments' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {payLoading ? (
            <div className="p-8 text-center text-sm text-gray-500">Loading payments...</div>
          ) : (
            <>
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 text-gray-600 font-medium">
                  <tr>
                    <th className="px-6 py-3">PAYMENT ID</th>
                    <th className="px-6 py-3">INVOICE ID</th>
                    <th className="px-6 py-3">AMOUNT</th>
                    <th className="px-6 py-3">DATE</th>
                    <th className="px-6 py-3">METHOD</th>
                    <th className="px-6 py-3">REFERENCE</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.length === 0 && (
                    <tr><td colSpan={6} className="px-6 py-8 text-center text-gray-500">No payments found.</td></tr>
                  )}
                  {payments.map((pay) => (
                    <tr key={pay.id} className="border-t">
                      <td className="px-6 py-3 font-semibold text-gray-800">{pay.payment_number}</td>
                      <td className="px-6 py-3">
                        <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">{pay.invoice_display_number || '-'}</span>
                      </td>
                      <td className="px-6 py-3 font-medium text-green-600">{fmt(pay.amount)}</td>
                      <td className="px-6 py-3">{pay.payment_date || '-'}</td>
                      <td className="px-6 py-3">
                        <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">{pay.method}</span>
                      </td>
                      <td className="px-6 py-3">{pay.reference || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-t">
                <span className="text-sm text-gray-500">
                  Showing <span className="font-medium">{payments.length === 0 ? 0 : (payPage - 1) * limit + 1}-{Math.min(payPage * limit, payTotal)}</span> of {payTotal}
                </span>
                <div className="flex items-center gap-1">
                  <button disabled={payPage <= 1} onClick={() => setPayPage(p => p - 1)} className="px-2 py-1 text-sm rounded hover:bg-gray-200 disabled:opacity-50">&lt;</button>
                  <span className="px-3 py-1 text-sm rounded bg-blue-600 text-white">{payPage}</span>
                  <button disabled={payPage >= payTotalPages} onClick={() => setPayPage(p => p + 1)} className="px-2 py-1 text-sm rounded hover:bg-gray-200 disabled:opacity-50">&gt;</button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
